import type { TimeOffBlock } from "./api";
import { isoDay } from "./overview";

/** Pure logic for the Time off tile: a rolling 12-month PTO count from Gusto hours. */

export const HOURS_PER_DAY = 8;
export const GOAL = { low: 25, high: 30 } as const;

export interface TimeOffSummary {
  windowStart: string; // YYYY-MM-DD, inclusive
  windowEnd: string; // today, inclusive
  /** Days used in the window: hours ÷ 8, from blocks that started on or before today */
  used: number;
  /** Days booked after today (any range the caller fetched) */
  booked: number;
  /** Blocks with no hours in Gusto — counted as 0, but worth showing */
  unknown: number;
  /** Fraction of the 12-month window elapsed (always 1 for a trailing window; kept for pace) */
  paceLow: number; // days a 25/yr pace would have used by now — equals 25 for a full trailing window
  paceHigh: number;
  blocks: TimeOffBlock[]; // in-window blocks, newest first
  upcoming: TimeOffBlock[]; // blocks after today, soonest first
}

export function daysOf(block: TimeOffBlock): number {
  return block.hours == null ? 0 : block.hours / HOURS_PER_DAY;
}

/** Same calendar day one year ago, plus one day, so the window is exactly 12 months ending today. */
export function windowStartFor(today: string): string {
  const d = new Date(`${today}T00:00:00`);
  d.setFullYear(d.getFullYear() - 1);
  d.setDate(d.getDate() + 1);
  return isoDay(d);
}

export function summarize(blocks: TimeOffBlock[], today: string): TimeOffSummary {
  const windowStart = windowStartFor(today);
  const inWindow = blocks
    .filter((b) => b.start <= today && b.end >= windowStart)
    .sort((a, b) => b.start.localeCompare(a.start));
  const upcoming = blocks
    .filter((b) => b.start > today)
    .sort((a, b) => a.start.localeCompare(b.start));
  const used = inWindow.reduce((sum, b) => sum + daysOf(b), 0);
  const booked = upcoming.reduce((sum, b) => sum + daysOf(b), 0);
  return {
    windowStart,
    windowEnd: today,
    used,
    booked,
    unknown: inWindow.filter((b) => b.hours == null).length,
    paceLow: GOAL.low,
    paceHigh: GOAL.high,
    blocks: inWindow,
    upcoming,
  };
}

/** "20" or "20.5" — whole days when whole, one decimal otherwise. */
export function formatDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** Short range label: "Jun 1–5", "Jun 29–Jul 2", "Jan 16". */
export function blockLabel(block: TimeOffBlock): string {
  const fmt = (day: string) => {
    const d = new Date(`${day}T00:00:00`);
    return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
  };
  if (block.start === block.end) return fmt(block.start);
  const sameMonth = block.start.slice(0, 7) === block.end.slice(0, 7);
  const endPart = sameMonth ? String(Number(block.end.slice(8))) : fmt(block.end);
  return `${fmt(block.start)}–${endPart}`;
}
